/**
 * useBehavioralTracker
 *
 * DOM-level tracker that emits plain scroll_depth_reached and exit_intent
 * events onto the FunnelEventBus (ADR 0004 D1/D3).
 *
 * Only active when isFunnelUiEventsEnabled() and a FunnelContext is present.
 * Re-registers on stepId change so thresholds reset per step.
 */
import { useEffect, useRef } from 'react';
import { useOptionalFunnelContext } from '../funnel-provider';
import { createEventEnvelope } from './envelope';
import { isFunnelUiEventsEnabled } from './feature-flag';

/** Scroll depth thresholds (%) at which events fire. Each fires at most once per step. */
const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;

export function useBehavioralTracker(stepId?: string): void {
  const funnel = useOptionalFunnelContext();
  const firedThresholdsRef = useRef<Set<number>>(new Set());
  const exitFiredRef = useRef(false);

  useEffect(() => {
    // Reset per step navigation
    firedThresholdsRef.current = new Set();
    exitFiredRef.current = false;
  }, [stepId]);

  useEffect(() => {
    if (!isFunnelUiEventsEnabled() || !funnel || !stepId) return;

    const bus = funnel.eventBus;
    const source = 'storefront-funnel-tracker';
    const correlationId = funnel.sessionId ?? undefined;
    const scope = funnel.config?.tenantId
      ? { tenantId: funnel.config.tenantId }
      : undefined;

    // ── Scroll depth tracker ──────────────────────────────────────────────────
    const onScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;
      const pct = Math.round((window.scrollY / scrollableHeight) * 100);

      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !firedThresholdsRef.current.has(threshold)) {
          firedThresholdsRef.current.add(threshold);
          bus.emit(
            createEventEnvelope(
              'scroll_depth_reached',
              source,
              {
                params: { scrollDepth: threshold },
                context: {
                  funnelId: funnel.assignedFunnelId ?? '',
                  stepId: stepId ?? '',
                  blockId: '',
                  blockType: '',
                  trigger: 'onView',
                  pageType: '',
                  currentUrl: typeof window !== 'undefined' ? window.location.href : '',
                },
              },
              { scope, correlationId },
            ),
          );
        }
      }
    };

    // ── Exit intent tracker ───────────────────────────────────────────────────
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY > 0 || exitFiredRef.current) return;
      exitFiredRef.current = true;
      bus.emit(
        createEventEnvelope(
          'exit_intent',
          source,
          {
            params: {},
            context: {
              funnelId: funnel.assignedFunnelId ?? '',
              stepId: stepId ?? '',
              blockId: '',
              blockType: '',
              trigger: 'onView',
              pageType: '',
              currentUrl: typeof window !== 'undefined' ? window.location.href : '',
            },
          },
          { scope, correlationId },
        ),
      );
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);
    // Check thresholds immediately in case the page is already scrolled
    // (e.g. after a step transition without movement, or page refresh).
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [funnel, stepId]);
}
